import { useState, useEffect } from "react";

/**
 * useBreakpoint
 * -------------
 * matchMedia-backed React hook that mirrors the breakpoints defined in
 * `src/assets/scss/_responsive.scss`. Use it to branch JS-side rendering
 * (e.g. swap a SplitPane for tabbed layout on mobile, hide Monaco minimap
 * on small screens, anchor the AI assistant as a FAB on phones, etc.).
 *
 * Breakpoints (Bootstrap 4-aligned):
 *   sm: >= 576px
 *   md: >= 768px
 *   lg: >= 992px
 *   xl: >= 1200px
 *
 * Returned shape:
 *   {
 *     // raw flags (true when window is at-or-above the breakpoint)
 *     smUp, mdUp, lgUp, xlUp,
 *     // semantic aliases
 *     isMobile,   //  < 768px
 *     isTablet,   //  768px - 991.98px
 *     isDesktop,  //  >= 992px
 *     // active label (one of "xs" | "sm" | "md" | "lg" | "xl")
 *     active,
 *     // current viewport width (px) for advanced cases
 *     width,
 *   }
 *
 * Example:
 *   const { isMobile, lgUp } = useBreakpoint();
 *   return lgUp ? <SplitPane>...</SplitPane> : <TabbedLayout>...</TabbedLayout>;
 */

const QUERIES = {
  smUp: "(min-width: 576px)",
  mdUp: "(min-width: 768px)",
  lgUp: "(min-width: 992px)",
  xlUp: "(min-width: 1200px)",
};

// SSR-safe initial read. Returns all-false when `window` is undefined.
const readMatches = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return { smUp: false, mdUp: false, lgUp: false, xlUp: false };
  }
  return {
    smUp: window.matchMedia(QUERIES.smUp).matches,
    mdUp: window.matchMedia(QUERIES.mdUp).matches,
    lgUp: window.matchMedia(QUERIES.lgUp).matches,
    xlUp: window.matchMedia(QUERIES.xlUp).matches,
  };
};

const readWidth = () =>
  typeof window === "undefined" ? 0 : window.innerWidth;

const labelFor = ({ smUp, mdUp, lgUp, xlUp }) => {
  if (xlUp) return "xl";
  if (lgUp) return "lg";
  if (mdUp) return "md";
  if (smUp) return "sm";
  return "xs";
};

export default function useBreakpoint() {
  const [matches, setMatches] = useState(readMatches);
  const [width, setWidth] = useState(readWidth);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    // One MediaQueryList per breakpoint — listening to these is cheaper than
    // a generic resize listener and only fires when crossing a breakpoint.
    const mqls = Object.entries(QUERIES).map(([key, query]) => {
      const mql = window.matchMedia(query);
      const handler = (e) =>
        setMatches((prev) => ({ ...prev, [key]: e.matches }));

      // Safari < 14 only supports the deprecated addListener API.
      if (mql.addEventListener) mql.addEventListener("change", handler);
      else mql.addListener(handler);

      return { mql, handler };
    });

    // Track raw width separately for components that need precise dimensions
    // (e.g., recomputing Monaco / SplitPane sizes on rotation).
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);

    // Re-sync on mount in case the SSR snapshot was stale.
    setMatches(readMatches());
    setWidth(readWidth());

    return () => {
      mqls.forEach(({ mql, handler }) => {
        if (mql.removeEventListener) mql.removeEventListener("change", handler);
        else mql.removeListener(handler);
      });
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const { smUp, mdUp, lgUp, xlUp } = matches;

  return {
    smUp,
    mdUp,
    lgUp,
    xlUp,
    isMobile: !mdUp,
    isTablet: mdUp && !lgUp,
    isDesktop: lgUp,
    active: labelFor(matches),
    width,
  };
}
